/**
 * engines/trendEngine.ts
 *
 * Lightweight EMA 9/21 directional trend model for MER Algo Footprints v5.1
 *
 * Purpose: add directional persistence awareness to the existing SMC/phase engine.
 * Trend context refines — it does NOT override — SMC structure or displacement logic.
 *
 * EMA 9:  fast trend (short-term momentum)
 * EMA 21: slow trend (structural direction)
 *
 * Outputs:
 *   trendDirection  — primary directional bias from EMA structure
 *   trendState      — behavioral sub-state of the trend
 *   trendStrength   — 0..1 quality of the trend signal
 *   alignmentScore  — 0..1 coherence between price, ema9, ema21, slopes
 *   ema9 / ema21    — current values
 *   slope9 / slope21 — rate of change (per bar, normalized)
 */

import { Candle, TrendDirection, TrendState, TrendResult } from '../../../../types';

// ─── Constants ────────────────────────────────────────────────────────────────

const EMA_FAST          = 9;
const EMA_SLOW          = 21;
const SLOPE_LOOKBACK    = 3;   // bars for slope calculation
const COMPRESSION_PCT   = 0.0008; // ema9 within 0.08% of ema21 = compression
const PULLBACK_PCT      = 0.003;  // price within 0.3% of ema9 in trend = pullback zone

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Exponential Moving Average — standard multiplier formula. */
function calcEMA(closes: number[], period: number): number[] {
  if (closes.length < period) return closes.map(() => closes[0]);
  const k   = 2 / (period + 1);
  const ema = new Array<number>(closes.length);
  ema[0] = closes[0];
  for (let i = 1; i < closes.length; i++) {
    ema[i] = closes[i] * k + ema[i - 1] * (1 - k);
  }
  return ema;
}

/**
 * Normalized slope: average (ema[i] - ema[i-1]) / ema[i-1] over lookback bars.
 * Returns a small signed fraction (e.g., +0.0012 = rising ~0.12%/bar).
 */
function calcSlope(series: number[], lookback: number): number {
  const end = series.length - 1;
  if (end < lookback) return 0;
  let sum = 0;
  for (let i = end - lookback + 1; i <= end; i++) {
    if (series[i - 1] > 0) sum += (series[i] - series[i - 1]) / series[i - 1];
  }
  return sum / lookback;
}

/** Classify trendState from EMA configuration and price position. */
function classifyTrend(
  price:    number,
  ema9:     number,
  ema21:    number,
  slope9:   number,
  slope21:  number,
): { direction: TrendDirection; state: TrendState; strength: number; alignment: number } {

  const spread      = Math.abs(ema9 - ema21) / ema21;
  const compressed  = spread < COMPRESSION_PCT;

  // Direction from EMA stack
  const ema9Above   = ema9 > ema21;
  const slope9Pos   = slope9 > 0;
  const slope21Pos  = slope21 > 0;
  const slope9Neg   = slope9 < 0;
  const slope21Neg  = slope21 < 0;

  // Slope disagreement signals transition
  const slopeAgreeBull = slope9Pos && slope21Pos;
  const slopeAgreeBear = slope9Neg && slope21Neg;
  const slopeDisagree  = !slopeAgreeBull && !slopeAgreeBear;

  // Price relative to EMAs
  const priceAboveEma9  = price > ema9;
  const priceBelowEma9  = price < ema9;
  const nearEma9        = Math.abs(price - ema9) / ema9 < PULLBACK_PCT;

  // ── State classification ──────────────────────────────────────────────────
  let direction:  TrendDirection;
  let state:      TrendState;
  let strength:   number;
  let alignment:  number;

  if (compressed || slopeDisagree) {
    // EMA lines close together or slopes disagree — unclear direction
    direction = 'neutral';
    state     = compressed ? 'compression' : 'transition';
    strength  = 0.1 + spread * 50;  // tiny strength during compression
    alignment = 0.2;

  } else if (ema9Above) {
    // Bullish EMA stack
    direction = 'bullish';

    if (slopeAgreeBull) {
      if (priceAboveEma9) {
        // Price above ema9, both slopes up — clean trend
        state    = 'trend';
        strength = Math.min(1, 0.5 + spread * 400 + slope9 * 200);
      } else if (nearEma9) {
        // Price dipped into ema9 zone but trend intact
        state    = 'pullback';
        strength = Math.min(1, 0.4 + spread * 300);
      } else {
        // Price below ema9 — possible deeper correction, trend weakening
        state    = 'transition';
        strength = Math.min(1, 0.25 + spread * 200);
      }
    } else {
      // Slope disagreement within bullish stack — transition
      state    = 'transition';
      strength = 0.2;
    }

    alignment = Math.min(1,
      (slopeAgreeBull ? 0.5 : 0.2) +
      (priceAboveEma9 ? 0.3 : nearEma9 ? 0.1 : 0) +
      Math.min(0.2, spread * 400)
    );

  } else {
    // Bearish EMA stack (ema9 < ema21)
    direction = 'bearish';

    if (slopeAgreeBear) {
      if (priceBelowEma9) {
        state    = 'trend';
        strength = Math.min(1, 0.5 + spread * 400 + Math.abs(slope9) * 200);
      } else if (nearEma9) {
        state    = 'pullback';
        strength = Math.min(1, 0.4 + spread * 300);
      } else {
        state    = 'transition';
        strength = Math.min(1, 0.25 + spread * 200);
      }
    } else {
      state    = 'transition';
      strength = 0.2;
    }

    alignment = Math.min(1,
      (slopeAgreeBear ? 0.5 : 0.2) +
      (priceBelowEma9 ? 0.3 : nearEma9 ? 0.1 : 0) +
      Math.min(0.2, spread * 400)
    );
  }

  return { direction, state, strength: Math.max(0, Math.min(1, strength)), alignment };
}

// ─── Main export ──────────────────────────────────────────────────────────────

export function analyzeTrend(candles: Candle[]): TrendResult | null {
  if (!candles || candles.length < EMA_SLOW + SLOPE_LOOKBACK + 2) return null;

  const closes = candles.map(c => c.close);
  const ema9s  = calcEMA(closes, EMA_FAST);
  const ema21s = calcEMA(closes, EMA_SLOW);

  const ema9  = ema9s[ema9s.length - 1];
  const ema21 = ema21s[ema21s.length - 1];

  const slope9  = calcSlope(ema9s,  SLOPE_LOOKBACK);
  const slope21 = calcSlope(ema21s, SLOPE_LOOKBACK);

  const curPrice = closes[closes.length - 1];

  const { direction, state, strength, alignment } = classifyTrend(
    curPrice, ema9, ema21, slope9, slope21
  );

  return {
    ema9,
    ema21,
    slope9,
    slope21,
    trendDirection: direction,
    trendState:     state,
    trendStrength:  strength,
    alignmentScore: alignment,
  };
}
